/**
 * SENTRY USER MANAGEMENT STRATEGY
 * ------------------------------
 * Because we use AWS Amplify for authentication, Sentry's default identity tracking
 * cannot automatically detect login/logout events.
 * * We manually manage the Sentry User Context to ensure:
 * 1. Post-login errors are associated with a specific user.
 * 2. Pre-login/Public errors remain anonymous.
 * 3. Privacy is maintained by clearing the user context on logout.
 */

import { type App } from 'vue';

import type { Router } from 'vue-router';

import type { ErrorEvent as SentryErrorEvent, EventHint } from '@sentry/vue';
import * as Sentry from '@sentry/vue';
import { getCurrentUser } from 'aws-amplify/auth';
import axios from 'axios';

import { environment } from '@/utils/environment';

interface AmplifyUser {
  userId: string;
  username: string; // email address
  signInDetails: {
    authFlowType: string;
    loginId: string;
  };
}

let amplifyUser: AmplifyUser | null = null;

/**
 * Browser-specific spellings of the same event: a request to the backend died
 * mid-flight (weak signal, device sleep, page unload). Reconnect/retry logic
 * covers these, so they are grouped and downgraded rather than paged as
 * separate error-level issues per browser.
 */
const TRANSIENT_NETWORK_PATTERNS: readonly RegExp[] = [
  /^Failed to fetch/i, // Chromium fetch
  /^NetworkError when attempting to fetch resource/i, // Firefox fetch
  /^Load failed/i, // Safari fetch
  /^network error$/i, // Chromium XHR / axios "Network Error"
  /^Error in input stream/i, // Firefox mid-stream drop
  /^The I\/O read operation failed/i // Safari mid-stream drop
];

/**
 * Detects transient network errors across browser and library spellings.
 * Checks axios error codes first (stable across browsers), then falls back to
 * regex patterns for native fetch/XHR errors which vary by engine. This ensures
 * that temporary network conditions (weak signal, device sleep, DNS blips) are
 * downgraded to warnings or dropped rather than reported as errors.
 */
const isTransientNetworkError = (hint: EventHint): boolean => {
  const err = hint.originalException;
  if (axios.isAxiosError(err)) {
    // Match axios failures by code, not message: codes are stable across
    // browsers whereas the human-readable strings above are not.
    if (
      [
        'ERR_NETWORK', // connection dropped mid-request
        'ECONNABORTED', // timeout / abort
        'ETIMEDOUT', // timeout (axios 1.x)
        'ENOTFOUND', // DNS failure
        'ECONNREFUSED', // connection refused (can be transient)
        'ECONNRESET', // connection reset by peer (transient in cloud)
        'EPIPE' // broken pipe (write to closed connection)
      ].includes(err.code || '')
    ) {
      return true;
    }
  }
  // AbortSignal.timeout() throws a DOMException named 'TimeoutError'; the
  // message wording differs per browser, so match the standardized name.
  // DOMException is not guaranteed to extend Error across browsers, so check
  // for the name before the instanceof Error guard below.
  if (typeof err === 'object' && err !== null && 'name' in err) {
    if (err.name === 'TimeoutError') return true;
  }

  if (!(err instanceof Error)) return false;

  return TRANSIENT_NETWORK_PATTERNS.some((pattern) =>
    pattern.test(err.message)
  );
};

/**
 * Sentry beforeSend hook that classifies transient network errors.
 * - Non-transient errors pass through unchanged (e.g., 500 errors, streaming unsupported)
 * - Transient errors while offline are dropped (user's connection, not actionable)
 * - Transient errors while online are downgraded to warnings and grouped by source (sse vs api)
 *   and, if available, retry attempt number — distinguishing one-off blips from persistent failures
 * - Visibility state is included to help diagnose backgrounded-tab disconnects
 */
const classifyTransientNetworkErrors = (
  event: SentryErrorEvent,
  hint: EventHint
): SentryErrorEvent | null => {
  if (!isTransientNetworkError(hint)) return event;

  // Provably offline — the user's connection, not an application failure. Drop entirely.
  if (!navigator.onLine) return null;

  event.level = 'warning';
  // One grouped issue per source instead of one per browser wording. Keeps
  // stream health (sse) and request health (api) separately trendable.
  const source = event.tags?.function === 'useSSE' ? 'sse' : 'api';
  const fingerprint = ['transient-network-error', source];

  // Include retry attempt in fingerprint if available, to distinguish transient
  // one-offs (first attempt succeeds on retry) from persistent failures.
  const err = hint.originalException;

  if (typeof err === 'object' && err !== null && 'attemptNumber' in err) {
    fingerprint.push(`attempt-${(err as any).attemptNumber}`);
  }

  event.fingerprint = fingerprint;

  event.extra = {
    ...event.extra,
    isOnline: navigator.onLine,
    visibility: document.visibilityState
  };
  return event;
};

// Sentry
export const loadSentry = async (app: App, router: Router) => {
  await setSentryUserContext();

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment,
    release: import.meta.env.VITE_VERSION,
    // Cancellations from intentional teardown (route change, logout,
    // superseded requests) are expected flow, never actionable errors.
    ignoreErrors: [
      /^AbortError:/,
      /^CanceledError:/,
      /^IntentionalLogoutError:/,
      'Fetch is aborted',
      'The operation was aborted',
      'signal is aborted'
    ],
    beforeSend: classifyTransientNetworkErrors,
    integrations: [
      Sentry.vueIntegration({
        tracingOptions: {
          // Automatically track component lifecycle for better error context
          trackComponents: true
        }
      }),
      Sentry.browserTracingIntegration({
        router
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true
      })
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5
  });
};

/**
 * Sets the Sentry user context.
 * Call this:
 * 1. During app initialization (loadSentry) to catch returning sessions.
 * 2. Immediately after successful login/signup in your auth composable.
 */
export const setSentryUserContext = async () => {
  try {
    amplifyUser = (await getCurrentUser()) as unknown as AmplifyUser;

    if (amplifyUser) {
      Sentry.setUser({
        email: amplifyUser.username,
        id: amplifyUser.userId,
        username: amplifyUser.signInDetails.loginId
      });

      // Set tags for filtering/searching
      // This helps identify if 2FA or specific auth flows are crashing the app
      if (amplifyUser.signInDetails?.authFlowType) {
        Sentry.setTag('auth.flow_type', amplifyUser.signInDetails.authFlowType);
      }

      return;
    }

    // Clear context if no user
    Sentry.setUser(null);
  } catch (error) {
    /*
     * No user is currently authenticated. This is expected for users who have not logged in yet.
     * Sentry will remain anonymous until the user logs in and this function is called again to set the user context.
     */
    console.warn(
      'No authenticated user found for Sentry context. User will be anonymous until login.',
      error
    );

    // Clear context on auth error
    Sentry.setUser(null);

    Sentry.setTag('auth.flow_type', null);
  }
};

/**
 * Explicitly clears the Sentry user.
 * Necessary in SPAs to prevent the "Ghost User" bug, where errors from a new
 * anonymous session are incorrectly attributed to the previous authenticated user.
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
  Sentry.setTag('auth_flow', null);
};

/**
 * Standardized error logger for better Sentry visibility.
 *
 * @param error - The error object
 * @param function - The name of the function where the error occurred
 * @param section - The section where the error occurred
 * @param level - The severity level (optional; Sentry uses defaults if omitted)
 * @param message - A custom message to provide additional context about the error
 * @param metadata - Any extra variables (like 'attempt' or 'userId')
 */
export const logAppError = ({
  error,
  functionName,
  section,
  level,
  message,
  metadata = {}
}: {
  error: unknown;
  functionName: string;
  section?: string;
  level?: 'fatal' | 'warning' | 'info' | 'debug';
  message?: string;
  metadata?: Record<string, any>;
}) => {
  Sentry.withScope((scope) => {
    // Always set the function tag
    scope.setTag('function', functionName);

    // Only set the section tag if it's provided
    if (section) {
      scope.setTag('section', section);
    }

    // Only override level if explicitly provided
    if (level) {
      scope.setLevel(level);
    }

    if (message) {
      metadata.contextMessage = message;
    }

    // Set the fingerprint for better grouping
    // This uses the function and section to distinguish between generic errors
    const fingerprint = ['{{ default }}', functionName];
    if (section) fingerprint.push(section);
    scope.setFingerprint(fingerprint);

    // Attach extra metadata
    if (metadata) {
      scope.setExtras(metadata);
    }

    Sentry.captureException(error);
  });
};
